import { useMutation } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Button } from '@/components/ui/button';
import { LoyaltyBrandMark } from './LoyaltyBrandMark';
import { completeLoyaltySignIn, startLoyaltySignIn } from '@/api/loyalty';
import { useTranslation } from '@/i18n';
import type { LoyaltyConfiguration } from '@/api/types';

/** The bit field the API sends: 1 phone, 2 email, 3 either. */
const PHONE = 1;
const EMAIL = 2;

interface LoyaltySignInProps {
    configuration: LoyaltyConfiguration;
    /** The programme's own mark. Null when the brand has not uploaded one - then its name is used. */
    logoUrl: string | null;
    accessToken: string;
    /** `signInToken` is null when the brand does not verify — that is a normal, signed-in state. */
    onSignedIn: (accountId: string, signInToken: string | null) => void;
    onCancel: () => void;
}

/**
 * Identifying the guest, and proving it when the brand asks.
 *
 * Two steps in one screen, because for most brands there is only one. `POST /loyalty/sign-in` is always
 * called and the answer decides whether a code is needed — the app never reads a configuration flag to work
 * that out, so a brand turning verification on takes effect without a release here.
 */
export function LoyaltySignIn({ configuration, logoUrl, accessToken, onSignedIn, onCancel }: LoyaltySignInProps) {
    const { t } = useTranslation();
    const identifierId = useId();
    const codeId = useId();

    const [identifier, setIdentifier] = useState('');
    const [code, setCode] = useState('');
    const [challengeId, setChallengeId] = useState<string | null>(null);
    const [maskedDestination, setMaskedDestination] = useState<string | null>(null);
    const [codeRejected, setCodeRejected] = useState(false);

    const start = useMutation({
        mutationFn: () => startLoyaltySignIn(accessToken, identifier.trim()),
        onSuccess: (result) => {
            if (!result.verificationRequired) {
                onSignedIn(identifier.trim(), null);
                return;
            }

            setChallengeId(result.challengeId);
            setMaskedDestination(result.maskedDestination);
        },
    });

    const verify = useMutation({
        mutationFn: () => completeLoyaltySignIn(accessToken, challengeId!, code.trim()),
        onSuccess: (result) => {
            // A wrong code is a 200 with `verified: false`, not an error. It is the commonest outcome after a
            // mistyped digit and belongs in the form, not in an error state.
            if (!result.verified) {
                setCodeRejected(true);
                return;
            }

            onSignedIn(identifier.trim(), result.signInToken);
        },
    });

    const awaitingCode = challengeId !== null;
    const busy = start.isPending || verify.isPending;

    /*
     * One field, labelled by what the brand actually accepts. The API takes the identifier exactly as typed
     * and hands it to the vendor verbatim, so nothing here tidies it up: a number this app reformatted would
     * verify one string and look up another.
     */
    const label = accepts(configuration.signInType, PHONE) && accepts(configuration.signInType, EMAIL)
        ? t('loyalty.identifier.either')
        : accepts(configuration.signInType, EMAIL)
          ? t('loyalty.identifier.email')
          : t('loyalty.identifier.phone');

    return (
        <form
            className="flex flex-col gap-6"
            onSubmit={(event) => {
                event.preventDefault();
                if (busy) return;

                if (awaitingCode) {
                    setCodeRejected(false);
                    verify.mutate();
                    return;
                }

                start.mutate();
            }}
        >
            <div className="flex flex-col gap-2 text-center">
                <LoyaltyBrandMark logoUrl={logoUrl} programName={configuration.programName} />

                {awaitingCode && (
                    <p className="text-[0.9375rem] text-muted-foreground text-pretty">
                        {/* The masked destination is the point of this sentence: it is how a guest spots a mistyped number. */}
                        {t('loyalty.codeSent', { destination: maskedDestination ?? '' })}
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-2">
                <label htmlFor={awaitingCode ? codeId : identifierId} className="font-medium">
                    {awaitingCode ? t('loyalty.code') : label}
                </label>

                {awaitingCode ? (
                    <input
                        id={codeId}
                        value={code}
                        onChange={(event) => setCode(event.target.value)}
                        // A numeric keypad, but still a text field: `type="number"` brings spinners, strips
                        // leading zeros, and a code is a string of digits rather than a quantity.
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        // The whole point of the field: browsers and iOS offer the code from the message.
                        autoFocus
                        className="min-h-12 rounded-xl border border-border bg-background px-4 text-lg tracking-widest tabular"
                    />
                ) : (
                    <input
                        id={identifierId}
                        value={identifier}
                        onChange={(event) => setIdentifier(event.target.value)}
                        inputMode={accepts(configuration.signInType, EMAIL) ? 'email' : 'tel'}
                        autoComplete={accepts(configuration.signInType, EMAIL) ? 'email' : 'tel'}
                        autoFocus
                        className="min-h-12 rounded-xl border border-border bg-background px-4 text-lg"
                    />
                )}
            </div>

            {codeRejected && (
                <p role="alert" className="text-[0.9375rem] font-medium text-destructive text-pretty">
                    {t('loyalty.codeWrong')}
                </p>
            )}

            {(start.isError || verify.isError) && (
                <p role="alert" className="text-[0.9375rem] font-medium text-destructive text-pretty">
                    {t('loyalty.error')}
                </p>
            )}

            <div className="flex flex-col gap-3">
                <Button
                    type="submit"
                    size="lg"
                    disabled={busy || (awaitingCode ? code.trim() === '' : identifier.trim() === '')}
                >
                    {busy ? t('loyalty.working') : awaitingCode ? t('loyalty.verify') : t('loyalty.continue')}
                </Button>

                <Button variant="ghost" size="lg" onClick={onCancel} disabled={busy} type="button">
                    {t('loyalty.skip')}
                </Button>
            </div>
        </form>
    );
}

function accepts(signInType: number, flag: number): boolean {
    return (signInType & flag) !== 0;
}
